import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
    FiCalendar, FiClock, FiCheck, FiX, FiFilter,
    FiMessageSquare, FiUser, FiChevronDown
} from 'react-icons/fi';
import '../styles/LeaveManagement.css';

const LeaveManagement = () => {
    const { user } = useAuth();
    const [activeFilter, setActiveFilter] = useState('pending');
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [commentInputs, setCommentInputs] = useState({});
    const [showCommentFor, setShowCommentFor] = useState(null);

    useEffect(() => {
        fetchLeaves();
    }, [activeFilter]);

    const fetchLeaves = async () => {
        try {
            setLoading(true);
            let res;
            if (activeFilter === 'my-approvals') {
                res = await api.get('/users/leaves/pending-approvals');
            } else {
                res = await api.get('/users/leaves/all');
            }
            let data = res.data.leaves || [];

            if (activeFilter !== 'my-approvals' && activeFilter !== 'all') {
                data = data.filter(l => l.status === activeFilter);
            }

            setLeaves(data);
        } catch (err) {
            console.error('Error fetching leaves:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (leaveId) => {
        try {
            setActionLoading(leaveId + '-approve');
            const comment = commentInputs[leaveId] || '';
            await api.put(`/users/leaves/${leaveId}/approve`, { comment });
            setCommentInputs(prev => ({ ...prev, [leaveId]: '' }));
            setShowCommentFor(null);
            fetchLeaves();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to approve');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (leaveId) => {
        const comment = commentInputs[leaveId] || '';
        if (!comment.trim()) {
            alert('Please provide a reason for rejection');
            setShowCommentFor(leaveId);
            return;
        }
        try {
            setActionLoading(leaveId + '-reject');
            await api.put(`/users/leaves/${leaveId}/reject`, { comment });
            setCommentInputs(prev => ({ ...prev, [leaveId]: '' }));
            setShowCommentFor(null);
            fetchLeaves();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to reject');
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const getLeaveTypeLabel = (type) => {
        const labels = {
            sick: 'Sick Leave',
            casual: 'Casual Leave',
            earned: 'Earned Leave',
            unpaid: 'Unpaid Leave',
            other: 'Other',
            half_day: 'Half Day',
            early_leave: 'Early Leave'
        };
        return labels[type] || type;
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'approved': return 'lm-approved';
            case 'rejected': return 'lm-rejected';
            default: return 'lm-pending';
        }
    };

    const getMyApprovalStatus = (leave) => {
        if (!leave.approvals) return null;
        return leave.approvals.find(a => String(a.approverId) === String(user?.id));
    };

    const getDayCount = (start, end) => {
        const s = new Date(start);
        const e = new Date(end);
        const diff = Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1;
        return diff;
    };

    const filters = [
        { key: 'my-approvals', label: 'Needs My Approval' },
        { key: 'pending', label: 'Pending' },
        { key: 'approved', label: 'Approved' },
        { key: 'rejected', label: 'Rejected' },
        { key: 'all', label: 'All' },
    ];

    return (
        <div className="lm-container">
            <div className="lm-header">
                <h2 className="lm-title">
                    <FiCalendar size={22} />
                    Leave Management
                </h2>
                <p className="lm-subtitle">Review and manage team leave requests</p>
            </div>

            {/* Filters */}
            <div className="lm-filters">
                {filters.map(f => (
                    <button
                        key={f.key}
                        className={`lm-filter-btn ${activeFilter === f.key ? 'active' : ''}`}
                        onClick={() => setActiveFilter(f.key)}
                    >
                        {f.label}
                        {f.key === 'my-approvals' && leaves.length > 0 && activeFilter === 'my-approvals' && (
                            <span className="lm-filter-count">{leaves.length}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Leave Cards */}
            <div className="lm-content">
                {loading ? (
                    <div className="lm-loading">
                        <div className="profile-spinner" />
                        <p>Loading leaves...</p>
                    </div>
                ) : leaves.length === 0 ? (
                    <div className="lm-empty">
                        <FiCalendar size={40} />
                        <p>No leave requests found</p>
                    </div>
                ) : (
                    <div className="lm-list">
                        {leaves.map(leave => {
                            const myApproval = getMyApprovalStatus(leave);
                            const canAct = myApproval && myApproval.status === 'pending' && leave.status === 'pending';
                            const days = getDayCount(leave.startDate, leave.endDate);

                            return (
                                <div key={leave._id} className={`lm-card ${canAct ? 'lm-card-actionable' : ''}`}>
                                    <div className="lm-card-top">
                                        <div className="lm-card-user">
                                            <div className="lm-user-avatar">
                                                {(leave.userName || 'U').charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="lm-user-name">{leave.userName || 'Unknown User'}</div>
                                                <div className="lm-user-meta">
                                                    {getLeaveTypeLabel(leave.leaveType)} • {leave.leaveType === 'half_day' || leave.leaveType === 'early_leave' ? '0.5' : days} day{days > 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <span className={`lm-status-badge ${getStatusClass(leave.status)}`}>
                                            {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                                        </span>
                                    </div>

                                    <div className="lm-card-dates">
                                        <FiClock size={14} />
                                        <span>{formatDate(leave.startDate)}</span>
                                        <span className="lm-date-arrow">→</span>
                                        <span>{formatDate(leave.endDate)}</span>
                                    </div>

                                    <div className="lm-card-reason">
                                        <strong>Reason:</strong> {leave.reason}
                                    </div>

                                    {/* Approval Checkpoints */}
                                    {leave.approvals && leave.approvals.length > 0 && (
                                        <div className="lm-approvals">
                                            <div className="lm-approvals-title">Approvers</div>
                                            <div className="lm-approval-chips">
                                                {leave.approvals.map((appr, idx) => (
                                                    <div
                                                        key={appr._id || idx}
                                                        className={`lm-approval-chip lm-approval-chip-${appr.status}`}
                                                        title={appr.comment ? `${appr.label}: "${appr.comment}"` : appr.label}
                                                    >
                                                        <span className="lm-chip-icon">
                                                            {appr.status === 'approved' ? '✓' : appr.status === 'rejected' ? '✗' : '○'}
                                                        </span>
                                                        <span className="lm-chip-name">{appr.approverName}</span>
                                                        <span className="lm-chip-role">{appr.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    {canAct && (
                                        <div className="lm-actions">
                                            {showCommentFor === leave._id && (
                                                <div className="lm-comment-box">
                                                    <FiMessageSquare size={14} />
                                                    <input
                                                        type="text"
                                                        placeholder="Add a comment (required for rejection)..."
                                                        value={commentInputs[leave._id] || ''}
                                                        onChange={(e) => setCommentInputs(prev => ({
                                                            ...prev, [leave._id]: e.target.value
                                                        }))}
                                                    />
                                                </div>
                                            )}

                                            <div className="lm-action-btns">
                                                <button
                                                    className="lm-comment-toggle"
                                                    onClick={() => setShowCommentFor(showCommentFor === leave._id ? null : leave._id)}
                                                >
                                                    <FiMessageSquare size={14} />
                                                    {showCommentFor === leave._id ? 'Hide Comment' : 'Add Comment'}
                                                </button>

                                                <button
                                                    className="lm-approve-btn"
                                                    onClick={() => handleApprove(leave._id)}
                                                    disabled={actionLoading === leave._id + '-approve'}
                                                >
                                                    {actionLoading === leave._id + '-approve' ? (
                                                        <span className="pw-btn-spinner" />
                                                    ) : (
                                                        <><FiCheck size={15} /> Approve</>
                                                    )}
                                                </button>

                                                <button
                                                    className="lm-reject-btn"
                                                    onClick={() => handleReject(leave._id)}
                                                    disabled={actionLoading === leave._id + '-reject'}
                                                >
                                                    {actionLoading === leave._id + '-reject' ? (
                                                        <span className="pw-btn-spinner" />
                                                    ) : (
                                                        <><FiX size={15} /> Reject</>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Show rejection comment on rejected leaves */}
                                    {leave.status === 'rejected' && leave.approvals?.some(a => a.status === 'rejected' && a.comment) && (
                                        <div className="lm-rejection-note">
                                            {leave.approvals.filter(a => a.status === 'rejected' && a.comment).map((a, i) => (
                                                <div key={i}>
                                                    <strong>{a.approverName} ({a.label}):</strong> "{a.comment}"
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeaveManagement;
